import classNames from 'classnames';
import { ROUTES } from 'Constants';
import dayjs from 'dayjs';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';

import { useJourneySchedule } from 'Global/Journey/Queries/useJourneySchedule';
import { Journey } from 'Global/Journey/Types/Journey';
import { appConfigStore } from 'Models/AppConfigStore';
import { useStopById } from 'Global/JourneyElements/Hooks/useStopsById';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import useStore from 'Hooks/useStore';
import { wizardModeOptions } from 'Models/Enums';
import React from 'react';
import { BookingPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { formatLongDateTime } from 'Util/_HumanWritten/DateUtils';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { TripBreakdownDescription } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/TripBreakdownDescription';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import LinkAsButton from 'Views/Components/_HumanWritten/LinkAsButton/LinkAsButton';
import Skeleton from 'Views/Components/_HumanWritten/Skeleton/Skeleton';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import { BookingWizardCartFields } from '../../BookingWizardTypes';
import { AdditionalItemLineItem } from './AdditionalItemLineItem';
import { Expander } from 'Views/Components/Expander/Expander';
import StyledQrCode from 'Views/Components/_HumanWritten/StyleQrCode';
import { Flex } from 'Views/Components/Flex/Flex';
import { checkInUrl } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/BookingDetailsQrCode';

export interface TripBreakdownProps {
	cartData: BookingWizardCartFields;
	departureTrip: boolean;
	bookingInfo?: BookingPricingResult;
	journey: Journey;
	shouldShowEditButtons?: boolean;
	canEditBooking?: boolean;
	handleEdit?: () => void;
	handleRemove?: () => void;
	isAlteration?: boolean;
	shouldShowPrice?: boolean;
	afterPayment?: boolean;
	isCurrentBooking: boolean;
	cartView: boolean;
	showQrCode?: boolean;
}

export function BookingBreakdown({
	cartData,
	departureTrip,
	bookingInfo,
	journey,
	shouldShowEditButtons = false,
	handleEdit,
	handleRemove,
	canEditBooking = true,
	isAlteration = false,
	shouldShowPrice = false,
	afterPayment = false,
	cartView,
	isCurrentBooking,
	showQrCode,
}: TripBreakdownProps) {
	const store = useStore();
	const { isMobile } = useDeviceDetect();
	const startLocationName = useStopById(journey.startStopId)?.location.name;
	const endLocationName = useStopById(journey.endStopId)?.location.name;
	const journeySchedule = useJourneySchedule(journey);

	const filteredBookingInfo = bookingInfo;

	if (isNullOrUndefined(filteredBookingInfo)) {
		return null;
	}

	filteredBookingInfo.priceLineItems = filteredBookingInfo.priceLineItems.filter(
		x => x.key !== 'FreightHandling' && x.key !== 'FreightBase',
	);

	// The server counts the trailer removal fee in this leg's total. The leg does not show the fee (the
	// summary card does, beneath the subtotal), so it comes back out here — otherwise the leg prints a
	// trailer refund of -$20.00 above a "Departing total" of $0.00 with nothing explaining the gap.
	const trailerRemovalFee =
		filteredBookingInfo.priceLineItems.find(x => x.key === 'TowOnCancellationFee')?.price ?? 0;

	// Same treatment as the trailer removal fee, for the same reason: what the check-in reduction gave
	// back belongs to the trip, what the customer forfeited for it does not.
	const checkInForfeitFee =
		filteredBookingInfo.priceLineItems.find(x => x.key === 'CheckInForfeitFee')?.price ?? 0;

	// And the general removal fee, which is the same kind of thing as the trailer one — a charge for
	// taking something off, not part of what the leg is worth. It is summed over every removed item, so
	// there is no single line it belongs beside; it belongs with the other fees under the subtotal.
	const removalFee = filteredBookingInfo.priceLineItems.find(x => x.key === 'RemovalFee')?.price ?? 0;

	const isAlterationMode = cartData.wizardData.wizardMode === wizardModeOptions.ALTERATION;

	// Sites with passenger classes get one row per (type, class) with its own price, instead of one
	// combined description beside one combined price. The server only sends rows that add up to the
	// passenger line, so when it sends none the combined row below is still the right thing to show.
	const passengerClassLines = appConfigStore.passengerClassesEnabled
		? (filteredBookingInfo.passengerClassLines ?? [])
		: [];
	const splitByClass = passengerClassLines.length > 0;

	const sumOfKeys = (...keys: string[]) =>
		filteredBookingInfo.priceLineItems
			.filter(x => keys.includes(x.key))
			.map(x => x.price)
			.reduce((a, b) => a + b, 0);

	const vehicleLineItem = filteredBookingInfo.priceLineItems.find(x => x.key === 'BaseCargo');
	const alterationFee = sumOfKeys('AlterationFee');

	let discountAmount = 0;

	if (filteredBookingInfo.priceLineItems.filter(x => x.key === 'Discount').length > 0) {
		discountAmount = filteredBookingInfo.priceLineItems
			.filter(x => x.key === 'Discount')
			.map(x => x.price)
			.reduce((a, b) => a + b, 0);
	}

	return (
		<div className={`${departureTrip ? 'departing' : 'return'} trip-card`}>
			<div className={classNames('booking-details-overview__container', { 'show-price': shouldShowPrice })}>
				<div className="booking-details-overview__header">
					<p className="route-name">
						{startLocationName}
						<Icon name="arrow-right" />
						{endLocationName}
					</p>
					{store.isManager && (
						<LinkAsButton
							to={ROUTES.FERRY_SCHEDULE_EDIT.url(journey.tripId, 'insights')}
							className="trip-details__date"
						>
							<Icon name="calendar" />
							{!journeySchedule.data && <Skeleton width="5rem" />}
							{journeySchedule.data && (
								<>{`${dayjs(journeySchedule.data.departureDateTime).format('DD MMM YYYY')}, ${formatStandardTime(journeySchedule.data.departureDateTime)}`}</>
							)}
						</LinkAsButton>
					)}
					{!store.isManager && (
						<p className="trip-details__date">
							<Icon name="calendar" />
							{!journeySchedule.data && <Skeleton width="5rem" />}
							{journeySchedule.data && (
								<>{`${formatLongDateTime(journeySchedule.data.departureDateTime)}`}</>
							)}
						</p>
					)}
				</div>
				<div className="booking-details-overview__actions">
					{shouldShowEditButtons && !isAlteration && (
						<>
							{canEditBooking && handleEdit && (
								<Button
									className="edit-button"
									colors={Colors.Grey}
									display={Display.Outline}
									onClick={handleEdit}
									newStyles
								>
									Edit
								</Button>
							)}
							{handleRemove && (
								<Button
									className="remove-button"
									colors={Colors.Red}
									display={Display.Outline}
									onClick={handleRemove}
									newStyles
									sizes={Sizes.Medium}
									icon={isMobile ? { icon: 'bin-delete', iconPos: 'icon-bottom' } : undefined}
								>
									{isMobile ? '' : 'Remove'}
								</Button>
							)}
						</>
					)}
				</div>
			</div>
			<If condition={afterPayment || cartView || (!cartView && isCurrentBooking)}>
				{!splitByClass && (
					<div className="base-price__container">
						<p className="booked-trip-passenger-details">
							<TripBreakdownDescription bookingInfo={filteredBookingInfo} />
						</p>
						<p className="additional-item__price">
							{formatPriceDisplay(
								sumOfKeys('Passenger', 'BaseCargo', 'BaseTowOn', 'AlterationFee'),
								isAlterationMode,
							)}
						</p>
					</div>
				)}
				{splitByClass && (
					<div className="base-price__container base-price__container--by-class">
						{vehicleLineItem && (
							<div className="base-price__row">
								<p className="additional-item__label">{vehicleLineItem.description}</p>
								<p className="additional-item__price">
									{formatPriceDisplay(sumOfKeys('BaseCargo', 'BaseTowOn'), isAlterationMode)}
								</p>
							</div>
						)}
						{passengerClassLines.map(line => (
							<div className="base-price__row" key={line.description}>
								<p className="additional-item__label">{line.description}</p>
								<p className="additional-item__price">
									{formatPriceDisplay(line.price, isAlterationMode)}
								</p>
							</div>
						))}
						{alterationFee !== 0 && (
							<div className="base-price__row">
								<p className="additional-item__label">Alteration fee</p>
								<p className="additional-item__price">
									{formatPriceDisplay(alterationFee, isAlterationMode)}
								</p>
							</div>
						)}
					</div>
				)}
				{/*
				  * The leg lists what the leg is worth. `TowOnCancellationFee`, `CheckInForfeitFee` and
				  * `RemovalFee` are left out on purpose and taken back off the leg total below with them:
				  * none is part of what the trip is worth — two are charges for taking something off the
				  * booking, the other is what the customer gave up for a reduction that could not be
				  * refunded. All three are presented once, in the summary card under the subtotal, with
				  * the other fees.
				  *
				  * Leaving the forfeit fee in here would be worse than untidy: it is sized to exactly the
				  * reduction it offsets, so the leg would show a refund and a charge that cancel out and
				  * a total of $0.00 — which is the very appearance the forfeit fee exists to replace.
				  */}
				<div className="additional-prices__container">
					{filteredBookingInfo?.priceLineItems
						.filter(
							x =>
								x.key !== 'Passenger' &&
								x.key !== 'BaseCargo' &&
								x.key !== 'BaseTowOn' &&
								x.key !== 'AlterationFee' &&
								x.key !== 'TowOnCancellationFee' &&
								x.key !== 'CheckInForfeitFee' &&
								x.key !== 'RemovalFee' &&
								x.key !== 'TicketPriceIncrease' &&
								x.key !== 'Discount',
						)
						.map(lineItem => {
							return (
								<AdditionalItemLineItem
									key={lineItem.key}
									lineItem={lineItem}
									isAlteration={cartData.wizardData.wizardMode === wizardModeOptions.ALTERATION}
								/>
							);
						})}
				</div>
				<div className="direction-total-cost">
					<div>
						<p>{departureTrip ? 'Departing total' : 'Returning total'}</p>
						<If condition={discountAmount > 0}>
							<p className="discount-amount">Discount</p>
						</If>
					</div>
					<div className="right-align">
						<p>
							{formatPriceDisplay(
								(filteredBookingInfo?.totalPrice ?? 0) -
									trailerRemovalFee -
									checkInForfeitFee -
									removalFee,
								cartData.wizardData.wizardMode === wizardModeOptions.ALTERATION,
							)}
						</p>
						<If condition={discountAmount > 0}>
							<p className="discount-amount">{formatPriceDisplay(discountAmount * -1, true)}</p>
						</If>
					</div>
				</div>
			</If>
			{showQrCode && bookingInfo?.qrCodeId && (
				<Expander>
					<Expander.ClickArea className="direction-total-cost">
						<p>View QR code</p>
						<Flex direction="row" align="center">
							<Expander.CollapsedLabel className="check-in-card__label">
								Show details
							</Expander.CollapsedLabel>
							<Expander.ExpandedLabel className="check-in-card__label">
								Hide details
							</Expander.ExpandedLabel>
							<Expander.Icon />
						</Flex>
					</Expander.ClickArea>
					<Expander.Body>
						<Flex className="direction-total-cost" direction="row" align="center">
							<StyledQrCode value={checkInUrl(bookingInfo.qrCodeId)} />
						</Flex>
					</Expander.Body>
				</Expander>
			)}
		</div>
	);
}
